import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth0 } from '@auth0/auth0-react';

const EtailPage = () => {
  const { isAuthenticated, user } = useAuth0();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedFaqs, setExpandedFaqs] = useState({});

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 }
  };

  // FAQ categories
  const categories = [
    { id: 'all', name: 'All Categories' },
    { id: 'general', name: 'General' },
    { id: 'account', name: 'Account & Billing' },
    { id: 'features', name: 'Features' },
    { id: 'technical', name: 'Technical' },
    { id: 'security', name: 'Security & Privacy' }
  ];

  // FAQ data
  const faqs = [
    {
      id: 1,
      question: 'What is Oyster Analytics?',
      answer: 'Oyster Analytics is a comprehensive data analytics platform designed to help businesses transform customer data into actionable insights. Our platform connects with your existing data sources, provides powerful visualization tools, and leverages AI to help you make data-driven decisions.',
      category: 'general'
    },
    {
      id: 2,
      question: 'How do I get started with Oyster?',
      answer: 'Getting started is easy! Simply sign up for a free account, connect your data sources using our intuitive interface, and start exploring insights. We offer guided tutorials and templates to help you get up and running quickly. Our customer success team is also available to assist with onboarding.',
      category: 'general'
    },
    {
      id: 3,
      question: 'Do you offer a free trial?',
      answer: 'Yes, we offer a 14-day free trial with full access to all features. No credit card is required to start your trial. After the trial period, you can choose the plan that best fits your needs.',
      category: 'account'
    },
    {
      id: 4,
      question: 'How does billing work?',
      answer: 'We offer monthly and annual subscription plans. Annual plans come with a 20% discount. You can upgrade, downgrade, or cancel your subscription at any time. We also offer custom enterprise pricing for larger organizations with specific needs.',
      category: 'account'
    },
    {
      id: 5,
      question: 'What data sources can I connect?',
      answer: 'Oyster supports a wide range of data sources including Salesforce, HubSpot, Google Analytics, Shopify, social media platforms, and many more. We also offer a REST API and direct database connections for custom integrations.',
      category: 'technical'
    },
    {
      id: 6,
      question: 'How secure is my data?',
      answer: 'Security is our top priority. We use industry-standard encryption for all data in transit and at rest. Our platform is SOC 2 Type II compliant, and we regularly undergo security audits. We never share your data with third parties without your permission.',
      category: 'security'
    },
    {
      id: 7,
      question: 'Can I export my data and reports?',
      answer: 'Yes, you can export data and reports in various formats including CSV, Excel, PDF, and Google Sheets. You can also schedule automated exports to be delivered to specified email addresses or integrated with your existing workflows.',
      category: 'features'
    },
    {
      id: 8,
      question: 'Do you offer customer support?',
      answer: 'Yes, we provide comprehensive support through multiple channels. Basic plans include email support with 24-hour response times. Professional and Enterprise plans include priority email support, live chat, and dedicated account managers for Enterprise customers.',
      category: 'general'
    },
    {
      id: 9,
      question: 'How often is the platform updated?',
      answer: 'We release updates and new features on a regular basis, typically every 2-4 weeks. Major feature releases are announced in advance through our product newsletter. We\'re constantly improving the platform based on customer feedback.',
      category: 'technical'
    },
    {
      id: 10,
      question: 'Can I customize dashboards and reports?',
      answer: 'Absolutely! Oyster offers powerful customization options for dashboards and reports. You can create custom visualizations, arrange widgets as needed, apply branded themes, and save templates for future use.',
      category: 'features'
    },
    {
      id: 11,
      question: 'What kind of AI capabilities does Oyster offer?',
      answer: 'Our platform includes various AI capabilities such as predictive analytics, anomaly detection, customer segmentation, and natural language processing for insights generation. These features help you uncover hidden patterns and make data-driven predictions.',
      category: 'features'
    },
    {
      id: 12,
      question: 'How do you handle data privacy regulations like GDPR and CCPA?',
      answer: "We're fully compliant with major data privacy regulations including GDPR and CCPA. We provide tools to help you manage user consent, data retention policies, and data subject access requests. Our platform includes features to anonymize personal data when necessary.",
      category: 'security'
    }
  ];

  // Toggle FAQ expansion
  const toggleFaq = (id) => {
    setExpandedFaqs(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Filter FAQs based on search and category
  const filteredFaqs = faqs.filter(faq => {
    const matchesSearch = faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="bg-white dark:bg-gray-900 min-h-screen">
      {/* Header Section */}
      <div className="relative overflow-hidden bg-gradient-to-b from-blue-100 to-white dark:from-blue-900 dark:to-gray-900  pt-24 ">


        <div className="container mx-auto px-4 relative z-1">
          <div className="max-w-3xl mx-auto text-center">
            <motion.h1
              className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-3"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Welcome! <span className="text-blue-600 dark:text-blue-400"> e-Tail</span>
            </motion.h1>
            <motion.p
              className="text-lg text-gray-600 dark:text-gray-300 mb-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              Register and browse our resources, or get in touch with our support team.
            </motion.p>


          </div>
        </div>

      </div>




      {/* Contact Section - Pre-fill email if authenticated */}
      <div className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              {/* Contact Form */}
              <motion.div
                className="bg-white dark:bg-gray-900 rounded-xl shadow-md p-8 border border-gray-100 dark:border-gray-700"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Send us a message</h3>
                <form>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
                      <input
                        type="text"
                        className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Your name"

                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mobile</label>
                      <input
                        type="mobile"
                        className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Your mobile "

                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                      <input
                        type="email"
                        className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Your email address"

                      />
                    </div>
                    {/* <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Subject</label>
                      <select className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <option value="">Select a subject</option>
                        <option value="billing">Billing Question</option>
                        <option value="technical">Technical Support</option>
                        <option value="feature">Feature Request</option>
                        <option value="account">Account Help</option>
                        <option value="other">Other</option>
                      </select>
                    </div> */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Message</label>
                      <textarea
                        className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 h-32"
                        placeholder="How can we help you?"
                      ></textarea>
                    </div>
                    <button
                      type="submit"
                      className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-300"
                    >
                      Send Message
                    </button>
                  </div>
                </form>
              </motion.div>

              {/* Contact Information */}
              <motion.div
                className="flex flex-col"
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <div className="bg-gradient-to-br from-red-600 to-gray-700 dark:from-blue-700 dark:to-indigo-900 rounded-xl shadow-md p-8 text-white mb-6 h-auto md:h-1/2">
                  <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <svg className="w-6 h-6 mr-3 mt-0.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <div>
                        <p className="text-blue-100 text-sm">Email</p>
                        <p className="font-medium">support@oysteranalytics.com</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <svg className="w-6 h-6 mr-3 mt-0.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      <div>
                        <p className="text-blue-100 text-sm">Phone</p>
                        <p className="font-medium">+1 (800) 123-4567</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <svg className="w-6 h-6 mr-3 mt-0.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <p className="text-blue-100 text-sm">Support Hours</p>
                        <p className="font-medium">Monday-Friday: 9am-6pm EST</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-900 rounded-xl shadow-md p-8 border border-gray-100 dark:border-gray-700 h-auto md:h-1/2">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Resources</h3>
                  <ul className="space-y-4">
                    <li>
                      <a href="#" className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
                        <svg className="w-5 h-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Knowledge Base
                      </a>
                    </li>
                    <li>
                      <a href="#" className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
                        <svg className="w-5 h-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        Video Tutorials
                      </a>
                    </li>
                    <li>
                      <a href="#" className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
                        <svg className="w-5 h-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                        </svg>
                        Webinars
                      </a>
                    </li>
                    <li>
                      <a href="#" className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
                        <svg className="w-5 h-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        Upcoming Events
                      </a>
                    </li>
                  </ul>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EtailPage; 